/*
 * assets/directory-finder.js
 * P4 dynamic brand/size-guide directories
 *
 * Shared client-side engine for the Brand directory (sections/brand-directory.liquid)
 * and the Product Sizing directory (sections/product-sizing-directory.liquid), both
 * rendered through snippets/directory-finder.liquid.
 *
 * This module never issues a fetch/XHR/catalogue/collection/Admin/Storefront API
 * request. Its only data source is the JSON already embedded by Liquid into a
 * <script type="application/json" data-directory-snapshot> tag inside each
 * directory root; everything below reads that string and nothing else.
 *
 * Structured as an IIFE that assigns to window.DWCDirectoryFinder so it still runs
 * unmodified as a plain browser <script> tag, and re-exported via module.exports so
 * the pure functions (classifyFreshness, validatePayload, evaluateSnapshot, ...) are
 * unit-testable from Node without a DOM.
 */
(function (root) {
  'use strict';

  var SCHEMA_VERSION = '1';
  var FRESH_SECONDS = 900;
  var STALE_SECONDS = 86400;
  var FUTURE_SKEW_SECONDS = 300;

  var CATEGORY_ORDER = ['clothing', 'shoes', 'accessories', 'new_arrivals', 'sale'];
  var CATEGORY_LABELS = {
    clothing: 'Clothing',
    shoes: 'Shoes',
    accessories: 'Accessories',
    new_arrivals: 'New Arrivals',
    sale: 'Sale'
  };
  var METRICS_ZERO_KEYS = ['broken_links', 'count_mismatches', 'duplicate_brands'];
  var METRICS_COUNT_KEYS = [
    'active_brands', 'size_guide_brands', 'aliases_normalized',
    'brands_hidden_unavailable', 'zero_category_links_suppressed',
    'broken_links', 'count_mismatches', 'duplicate_brands'
  ];

  // ---------------------------------------------------------------------
  // Freshness
  // ---------------------------------------------------------------------

  // age = now - generated_at, in seconds. "now" is always injected so tests are
  // deterministic; it is never read from Date.now() inside this function.
  function classifyFreshness(generatedAtIso, nowMs) {
    if (typeof generatedAtIso !== 'string' || generatedAtIso.trim() === '') return 'invalid';
    var generatedMs = Date.parse(generatedAtIso);
    if (isNaN(generatedMs)) return 'invalid';
    var now = typeof nowMs === 'number' ? nowMs : Date.now();
    var ageSeconds = (now - generatedMs) / 1000;
    if (ageSeconds < -FUTURE_SKEW_SECONDS) return 'invalid';
    if (ageSeconds <= FRESH_SECONDS) return 'fresh';
    if (ageSeconds <= STALE_SECONDS) return 'stale';
    return 'expired';
  }

  // ---------------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------------

  function normalizeName(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function isNonNegativeSafeInteger(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }

  // Same-origin relative storefront paths only. Rejects absolute URLs with a
  // scheme, protocol-relative URLs, control characters, ".." traversal segments
  // and malformed percent-encoding.
  //
  // Backslashes are rejected outright, not just a leading "//": browsers
  // normalize "\" to "/" when parsing URLs against a special (http/https)
  // base, so a value like "/\evil.com" would otherwise pass the "no leading
  // //" check here but resolve to protocol-relative "//evil.com" at render
  // time — the exact class of bypass this validator exists to prevent.
  function isValidRelativeUrl(value) {
    if (typeof value !== 'string' || value === '') return false;
    if (value.charAt(0) !== '/') return false;
    if (value.charAt(1) === '/') return false;
    if (value.indexOf('\\') !== -1) return false;
    if (/[\x00-\x1F\x7F]/.test(value)) return false;
    if (/%(?![0-9A-Fa-f]{2})/.test(value)) return false;
    var segments = value.split('/');
    for (var i = 0; i < segments.length; i += 1) {
      if (segments[i] === '..') return false;
    }
    try {
      decodeURIComponent(value);
    } catch (error) {
      return false;
    }
    return true;
  }

  function countCombinedAliases(setA, setB) {
    var combined = {};
    var key;
    for (key in setA) if (Object.prototype.hasOwnProperty.call(setA, key)) combined[key] = true;
    for (key in setB) if (Object.prototype.hasOwnProperty.call(setB, key)) combined[key] = true;
    return Object.keys(combined).length;
  }

  // ---------------------------------------------------------------------
  // Payload validation
  // ---------------------------------------------------------------------

  // Validates one brand's categories array: exact Clothing/Shoes/Accessories/
  // New Arrivals/Sale order, valid keys, positive counts only (a zero-count
  // category must never be present), valid labels and URLs. Returns a map of
  // {categoryKey: true} present on this brand, or null if invalid.
  function validateBrandCategories(categories) {
    if (!Array.isArray(categories)) return null;
    var lastOrder = -1;
    var present = {};
    for (var i = 0; i < categories.length; i += 1) {
      var category = categories[i];
      if (!category || typeof category !== 'object') return null;
      var order = CATEGORY_ORDER.indexOf(category.key);
      if (order === -1) return null;
      if (order <= lastOrder) return null;
      lastOrder = order;
      if (!isNonNegativeSafeInteger(category.count) || category.count <= 0) return null;
      if (!isNonEmptyString(category.label)) return null;
      if (!isValidRelativeUrl(category.url)) return null;
      present[category.key] = true;
    }
    return present;
  }

  function validateBrandEntries(brands) {
    var normalizedNames = {};
    var nameOwner = {};
    var categoryBrandCounts = { clothing: 0, shoes: 0, accessories: 0, new_arrivals: 0, sale: 0 };
    var previousNormalized = null;

    for (var i = 0; i < brands.length; i += 1) {
      var brand = brands[i];
      if (!brand || typeof brand !== 'object') return { valid: false, reason: 'brand-not-object' };
      if (!isNonEmptyString(brand.canonical_name)) return { valid: false, reason: 'brand-name-missing' };

      var normName = normalizeName(brand.canonical_name);
      if (normalizedNames[normName]) return { valid: false, reason: 'duplicate-brand' };
      normalizedNames[normName] = true;

      if (previousNormalized !== null && normName < previousNormalized) {
        return { valid: false, reason: 'brands-not-sorted' };
      }
      previousNormalized = normName;

      if (nameOwner[normName] !== undefined && nameOwner[normName] !== normName) {
        return { valid: false, reason: 'alias-ambiguous' };
      }
      nameOwner[normName] = normName;

      if (!Array.isArray(brand.aliases) || !brand.aliases.every(isNonEmptyString)) {
        return { valid: false, reason: 'brand-aliases-invalid' };
      }
      for (var a = 0; a < brand.aliases.length; a += 1) {
        var normAlias = normalizeName(brand.aliases[a]);
        if (normAlias === '') return { valid: false, reason: 'alias-empty' };
        if (nameOwner[normAlias] !== undefined && nameOwner[normAlias] !== normName) {
          return { valid: false, reason: 'alias-ambiguous' };
        }
        nameOwner[normAlias] = normName;
      }

      if (!isValidRelativeUrl(brand.destination_url)) return { valid: false, reason: 'brand-url-invalid' };
      if (!isNonNegativeSafeInteger(brand.available_product_count)) return { valid: false, reason: 'brand-count-invalid' };

      var presentCategories = validateBrandCategories(brand.categories);
      if (!presentCategories) return { valid: false, reason: 'brand-categories-invalid' };
      for (var key in presentCategories) {
        if (Object.prototype.hasOwnProperty.call(presentCategories, key)) categoryBrandCounts[key] += 1;
      }
    }

    var aliasNormalizedSet = {};
    for (var token in nameOwner) {
      if (Object.prototype.hasOwnProperty.call(nameOwner, token) && token !== nameOwner[token]) {
        aliasNormalizedSet[token] = true;
      }
    }

    return {
      valid: true,
      normalizedNames: normalizedNames,
      aliasNormalizedSet: aliasNormalizedSet,
      categoryBrandCounts: categoryBrandCounts
    };
  }

  function validateSizeGuideEntries(sizeGuides) {
    var normalizedNames = {};
    var nameOwner = {};
    var previousNormalized = null;

    for (var i = 0; i < sizeGuides.length; i += 1) {
      var entry = sizeGuides[i];
      if (!entry || typeof entry !== 'object') return { valid: false, reason: 'size-guide-not-object' };
      if (!isNonEmptyString(entry.canonical_name)) return { valid: false, reason: 'size-guide-name-missing' };

      var normName = normalizeName(entry.canonical_name);
      if (normalizedNames[normName]) return { valid: false, reason: 'duplicate-brand' };
      normalizedNames[normName] = true;

      if (previousNormalized !== null && normName < previousNormalized) {
        return { valid: false, reason: 'size-guides-not-sorted' };
      }
      previousNormalized = normName;

      if (nameOwner[normName] !== undefined && nameOwner[normName] !== normName) {
        return { valid: false, reason: 'alias-ambiguous' };
      }
      nameOwner[normName] = normName;

      if (!Array.isArray(entry.aliases) || !entry.aliases.every(isNonEmptyString)) {
        return { valid: false, reason: 'size-guide-aliases-invalid' };
      }
      for (var a = 0; a < entry.aliases.length; a += 1) {
        var normAlias = normalizeName(entry.aliases[a]);
        if (normAlias === '') return { valid: false, reason: 'alias-empty' };
        if (nameOwner[normAlias] !== undefined && nameOwner[normAlias] !== normName) {
          return { valid: false, reason: 'alias-ambiguous' };
        }
        nameOwner[normAlias] = normName;
      }

      if (!isNonEmptyString(entry.page_handle)) return { valid: false, reason: 'size-guide-handle-invalid' };
      if (!isValidRelativeUrl(entry.url)) return { valid: false, reason: 'size-guide-url-invalid' };
      if (entry.url !== '/pages/' + entry.page_handle) return { valid: false, reason: 'size-guide-url-mismatch' };
      if (!isNonNegativeSafeInteger(entry.available_product_count)) return { valid: false, reason: 'size-guide-count-invalid' };
    }

    var aliasNormalizedSet = {};
    for (var token in nameOwner) {
      if (Object.prototype.hasOwnProperty.call(nameOwner, token) && token !== nameOwner[token]) {
        aliasNormalizedSet[token] = true;
      }
    }

    return { valid: true, normalizedNames: normalizedNames, aliasNormalizedSet: aliasNormalizedSet };
  }

  function validateMetrics(metrics, brandsLength, sizeGuidesLength, brandCheck, sizeGuideCheck) {
    if (!metrics || typeof metrics !== 'object') return { valid: false, reason: 'metrics-missing' };

    for (var i = 0; i < METRICS_COUNT_KEYS.length; i += 1) {
      var metricKey = METRICS_COUNT_KEYS[i];
      if (!isNonNegativeSafeInteger(metrics[metricKey])) {
        return { valid: false, reason: 'metrics-field-invalid:' + metricKey };
      }
    }

    if (!metrics.category_links || typeof metrics.category_links !== 'object') {
      return { valid: false, reason: 'metrics-category-links-missing' };
    }
    for (var c = 0; c < CATEGORY_ORDER.length; c += 1) {
      var categoryKey = CATEGORY_ORDER[c];
      if (!isNonNegativeSafeInteger(metrics.category_links[categoryKey])) {
        return { valid: false, reason: 'metrics-category-link-invalid:' + categoryKey };
      }
    }

    if (metrics.active_brands !== brandsLength) return { valid: false, reason: 'active-brands-mismatch' };
    if (metrics.size_guide_brands !== sizeGuidesLength) return { valid: false, reason: 'size-guide-brands-mismatch' };

    var combinedAliasCount = countCombinedAliases(brandCheck.aliasNormalizedSet, sizeGuideCheck.aliasNormalizedSet);
    if (metrics.aliases_normalized !== combinedAliasCount) return { valid: false, reason: 'aliases-normalized-mismatch' };

    for (var k = 0; k < CATEGORY_ORDER.length; k += 1) {
      var key = CATEGORY_ORDER[k];
      if (metrics.category_links[key] !== brandCheck.categoryBrandCounts[key]) {
        return { valid: false, reason: 'category-link-mismatch:' + key };
      }
    }

    for (var z = 0; z < METRICS_ZERO_KEYS.length; z += 1) {
      if (metrics[METRICS_ZERO_KEYS[z]] !== 0) return { valid: false, reason: 'metrics-must-be-zero:' + METRICS_ZERO_KEYS[z] };
    }

    return { valid: true };
  }

  // Validates the full `payload` object of the dwc_directory_index metaobject
  // (the shape documented in docs/p4-dynamic-directories/architecture.md). Any
  // violation invalidates the whole snapshot (fail closed) — this function never
  // partially accepts a payload.
  function validatePayload(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return { valid: false, reason: 'payload-not-object' };
    }
    if (!Array.isArray(payload.brands) || !Array.isArray(payload.size_guides)) {
      return { valid: false, reason: 'missing-arrays' };
    }

    var brandCheck = validateBrandEntries(payload.brands);
    if (!brandCheck.valid) return brandCheck;

    var sizeGuideCheck = validateSizeGuideEntries(payload.size_guides);
    if (!sizeGuideCheck.valid) return sizeGuideCheck;

    return validateMetrics(payload.metrics, payload.brands.length, payload.size_guides.length, brandCheck, sizeGuideCheck);
  }

  // ---------------------------------------------------------------------
  // Snapshot evaluation (schema + freshness + payload, combined)
  // ---------------------------------------------------------------------

  // Combines schema/freshness/payload checks into one of:
  //   fresh | stale | expired | invalid | malformed | missing
  // "now" (nowMs) is always injected, never read from Date.now() in here.
  function evaluateSnapshot(rawText, nowMs) {
    if (typeof rawText !== 'string' || rawText.trim() === '') {
      return { state: 'missing' };
    }

    var snapshot;
    try {
      snapshot = JSON.parse(rawText);
    } catch (error) {
      return { state: 'malformed' };
    }
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
      return { state: 'malformed' };
    }
    if (snapshot.schema_version !== SCHEMA_VERSION) {
      return { state: 'invalid' };
    }

    var freshness = classifyFreshness(snapshot.generated_at, nowMs);
    if (freshness === 'invalid') {
      return { state: 'invalid' };
    }

    var validation = validatePayload(snapshot.payload);
    if (!validation.valid) {
      return { state: 'invalid', reason: validation.reason };
    }

    return { state: freshness, payload: snapshot.payload, generatedAt: snapshot.generated_at };
  }

  // ---------------------------------------------------------------------
  // Rendering helpers (pure data shaping — DOM-agnostic)
  // ---------------------------------------------------------------------

  // A brand with no available stock, or with no positive-count categories, must
  // never render even if it slipped into an otherwise-valid payload.
  function getRenderableBrands(payload, includeCounts) {
    return payload.brands
      .filter(function (brand) { return brand.available_product_count > 0 && brand.categories.length > 0; })
      .map(function (brand) {
        return {
          name: brand.canonical_name,
          aliases: brand.aliases,
          url: brand.destination_url,
          categories: brand.categories.map(function (category) {
            return { key: category.key, label: CATEGORY_LABELS[category.key] || category.label, count: includeCounts === false ? null : category.count, url: category.url };
          })
        };
      });
  }

  function getRenderableSizeGuides(payload) {
    return payload.size_guides.map(function (entry) {
      return { name: entry.canonical_name, aliases: entry.aliases, url: entry.url };
    });
  }

  function buildSearchText(name, aliases) {
    return [name].concat(aliases || []).join(' ').toLowerCase();
  }

  // ---------------------------------------------------------------------
  // DOM wiring (browser only)
  // ---------------------------------------------------------------------

  function buildBrandItem(doc, entry) {
    var item = doc.createElement('li');
    item.className = 'dwc-directory__item';
    item.setAttribute('data-directory-item', '');
    item.setAttribute('data-directory-name', buildSearchText(entry.name, entry.aliases));

    var link = doc.createElement('a');
    link.className = 'dwc-directory__link';
    link.href = entry.url;
    link.textContent = entry.name;
    item.appendChild(link);

    if (entry.categories.length) {
      var list = doc.createElement('ul');
      list.className = 'dwc-directory__categories';
      entry.categories.forEach(function (category) {
        var categoryItem = doc.createElement('li');
        categoryItem.className = 'dwc-directory__category';
        var categoryLink = doc.createElement('a');
        categoryLink.href = category.url;
        categoryLink.textContent = category.label + (category.count === null ? '' : ' (' + category.count + ')');
        categoryItem.appendChild(categoryLink);
        list.appendChild(categoryItem);
      });
      item.appendChild(list);
    }

    return item;
  }

  function buildSizingItem(doc, entry) {
    var item = doc.createElement('li');
    item.className = 'dwc-directory__item';
    item.setAttribute('data-directory-item', '');
    item.setAttribute('data-directory-name', buildSearchText(entry.name, entry.aliases));

    var link = doc.createElement('a');
    link.className = 'dwc-directory__link';
    link.href = entry.url;
    link.textContent = entry.name;
    item.appendChild(link);

    return item;
  }

  function setUnavailable(root, state) {
    root.setAttribute('data-directory-index-state', state);
    root.removeAttribute('data-directory-generated-at');

    var list = root.querySelector('[data-directory-list]');
    var unavailable = root.querySelector('[data-directory-unavailable]');
    var input = root.querySelector('[data-directory-input]');
    var status = root.querySelector('[data-directory-status]');
    var empty = root.querySelector('[data-directory-empty]');

    if (list) { list.hidden = true; list.textContent = ''; }
    if (unavailable) unavailable.hidden = false;
    if (empty) empty.hidden = true;
    if (input) input.disabled = true;
    if (status) status.textContent = '';
  }

  function wireFilter(root, mode, itemLabel) {
    var input = root.querySelector('[data-directory-input]');
    var list = root.querySelector('[data-directory-list]');
    var status = root.querySelector('[data-directory-status]');
    var empty = root.querySelector('[data-directory-empty]');
    if (!input || !list) return;

    var items = Array.prototype.slice.call(list.querySelectorAll('[data-directory-item]'));

    function apply() {
      var query = (input.value || '').trim().toLowerCase();
      var shown = 0;
      items.forEach(function (item) {
        var haystack = item.getAttribute('data-directory-name') || '';
        var match = query === '' || haystack.indexOf(query) !== -1;
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (status) status.textContent = shown + ' ' + (shown === 1 ? itemLabel : itemLabel + 's');
      if (empty) empty.hidden = shown !== 0;
    }

    input.addEventListener('input', apply);
    input.addEventListener('search', apply);
    apply();
  }

  function setAvailable(root, mode, state, generatedAt, entries) {
    root.setAttribute('data-directory-index-state', state);
    if ((state === 'stale' || state === 'expired') && generatedAt) {
      root.setAttribute('data-directory-generated-at', generatedAt);
    } else {
      root.removeAttribute('data-directory-generated-at');
    }

    var doc = root.ownerDocument;
    var list = root.querySelector('[data-directory-list]');
    var unavailable = root.querySelector('[data-directory-unavailable]');
    var input = root.querySelector('[data-directory-input]');

    if (unavailable) unavailable.hidden = true;
    if (!list) return;

    list.textContent = '';
    entries.forEach(function (entry) {
      list.appendChild(mode === 'brands' ? buildBrandItem(doc, entry) : buildSizingItem(doc, entry));
    });
    list.hidden = entries.length === 0;
    if (input) input.disabled = false;

    wireFilter(root, mode, mode === 'brands' ? 'brand' : 'size guide');
  }

  // Prefers the server-rendered timestamp Liquid stamps onto the root element
  // (seconds since epoch) over the visitor's own browser clock, per the
  // freshness contract: comparisons must not trust an untrusted customer
  // clock. Falls back to Date.now() only if that attribute is absent or
  // unparseable.
  function resolveNowMs(root) {
    var serverNow = root.getAttribute('data-directory-server-now');
    var parsed = serverNow ? parseInt(serverNow, 10) : NaN;
    return isNaN(parsed) ? Date.now() : parsed * 1000;
  }

  function initRoot(root) {
    var mode = root.getAttribute('data-directory-mode') === 'sizing' ? 'sizing' : 'brands';
    var snapshotEl = root.querySelector('[data-directory-snapshot]');
    var raw = snapshotEl ? snapshotEl.textContent : '';
    var result = evaluateSnapshot(raw, resolveNowMs(root));

    if (result.state === 'fresh' || result.state === 'stale' || result.state === 'expired') {
      var entries = mode === 'brands' ? getRenderableBrands(result.payload, result.state === 'fresh') : getRenderableSizeGuides(result.payload);
      setAvailable(root, mode, result.state, result.generatedAt, entries);
    } else {
      setUnavailable(root, result.state);
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-directory-finder]');
    for (var i = 0; i < roots.length; i += 1) initRoot(roots[i]);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  }

  root.DWCDirectoryFinder = {
    classifyFreshness: classifyFreshness,
    validatePayload: validatePayload,
    evaluateSnapshot: evaluateSnapshot,
    isValidRelativeUrl: isValidRelativeUrl,
    normalizeName: normalizeName,
    getRenderableBrands: getRenderableBrands,
    getRenderableSizeGuides: getRenderableSizeGuides,
    CATEGORY_ORDER: CATEGORY_ORDER
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = root.DWCDirectoryFinder;
  }
})(typeof window !== 'undefined' ? window : globalThis);